/**
 * Host-aware app mode + tenant branding.
 *
 * Two modes derived from the URL hostname:
 *  - "platform"  → admin.qrewhr.com (or PLATFORM_HOSTS env)  → only Master Panel / Plans / Billing
 *  - "tenant"    → everything else (acme.qrewhr.com, app.qrewhr.com, localhost) → tenant pages
 *
 * Subdomain branding: on tenant mode, login page resolves tenant by host via /api/public/tenant-by-host
 * to show the right logo/colors/software_name BEFORE the user authenticates.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const PLATFORM_SUBDOMAINS = ["admin", "platform", "master"];

export function getAppMode() {
  if (typeof window === "undefined") return "tenant";
  const host = window.location.hostname.toLowerCase();
  const sub = host.split(".")[0];
  if (PLATFORM_SUBDOMAINS.includes(sub)) return "platform";
  return "tenant";
}

export function isPlatformHost() { return getAppMode() === "platform"; }

export function useTenantBranding() {
  const [branding, setBranding] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    api.get(`/public/tenant-by-host?host=${encodeURIComponent(host)}`)
      .then(({ data }) => {
        setTenant({ id: data.tenant_id, slug: data.tenant_slug, name: data.tenant_name });
        setBranding(data.branding);
        // Apply CSS vars so the whole login flow + first paint are branded.
        if (data.branding && typeof document !== "undefined") {
          const r = document.documentElement;
          if (data.branding.primary_color) r.style.setProperty("--brand-primary", data.branding.primary_color);
          if (data.branding.accent_color) r.style.setProperty("--brand-accent", data.branding.accent_color);
          // Update window title
          document.title = `${data.branding.display_name || data.branding.software_name || "QrewHR"} — Login`;
        }
      })
      .catch(() => { /* fall back to default branding silently */ })
      .finally(() => setLoading(false));
  }, []);

  return { branding, tenant, loading };
}
